import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';

import HomePage from './components/HomePage/HomePage';

import GlobalPresence from './components/Presence/GlobalPresence';
import ScrollToTop from "./components/ScrollToTop";

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import './App.css';
import lazyWithRetry from './utils/lazyWithRetry';

// Route-level code splitting: only the home page (and the always-on presence
// pinger) ship in the main bundle; every other page loads its own code the
// first time it is opened.
const LandingPage = lazyWithRetry(() => import('./components/LandingPage/LandingPage'));
const Login = lazyWithRetry(() => import('./components/auth/Login'));
const SuperAdminLogin = lazyWithRetry(() => import('./components/auth/SuperAdminLogin'));
const Register = lazyWithRetry(() => import('./components/auth/Register'));
const VerifyEmail = lazyWithRetry(() => import('./components/auth/VerifyEmail'));
const ForgotPassword = lazyWithRetry(() => import('./components/auth/ForgotPassword'));
const Home = lazyWithRetry(() => import('./components/Home/Home'));
const ChangeUserRole = lazyWithRetry(() => import('./components/Admin/ChangeUserRole'));
const SubscribeMock = lazyWithRetry(() => import('./components/payments/SubscribeMock'));
const Payments = lazyWithRetry(() => import('./components/payments/Payments'));
const SubscriptionCheckout = lazyWithRetry(() => import('./components/payments/SubscriptionCheckout'));
const CreatedCourses = lazyWithRetry(() => import('./components/Courses/CreatedCourses'));
const UploadCourse = lazyWithRetry(() => import('./components/Courses/UploadCourse'));
const Layout = lazyWithRetry(() => import('./components/Layout/Layout'));
const BrowseCourses = lazyWithRetry(() => import('./components/Courses/BrowseCourses'));
const ShoppingCart = lazyWithRetry(() => import('./components/Cart/ShoppingCart'));
const EnrollmentPreview = lazyWithRetry(() => import('./components/Enroll/EnrollmentPreview'));
const MyCourses = lazyWithRetry(() => import('./components/Courses/MyCourses'));
const CourseLearn = lazyWithRetry(() => import('./components/Courses/CourseLearn'));
const ManageCourses = lazyWithRetry(() => import('./components/Admin/ManageCourses'));
const ManageUsers = lazyWithRetry(() => import('./components/Admin/ManageUsers'));
const ModerateContent = lazyWithRetry(() => import('./components/Admin/ModerateContent'));
const AdminLiveSessions = lazyWithRetry(() => import('./components/Admin/LiveSessions'));
const Students = lazyWithRetry(() => import('./components/Instructor/Students'));
const CourseAnalytics = lazyWithRetry(() => import('./components/Instructor/CourseAnalytics'));
const StudentPayments = lazyWithRetry(() => import('./components/Instructor/StudentPayments'));
const Settings = lazyWithRetry(() => import('./components/Settings/Settings'));
const Certifications = lazyWithRetry(() => import('./components/Student/Certifications'));
const TopPerforming = lazyWithRetry(() => import('./components/Instructor/TopPerforming'));
const LiveSessions = lazyWithRetry(() => import('./components/Instructor/LiveSessions'));
const SessionRoom = lazyWithRetry(() => import('./components/LiveSessions/SessionRoom'));
const Calendar = lazyWithRetry(() => import('./components/Instructor/Calendar'));
const SearchResults = lazyWithRetry(() => import('./components/Search/SearchResults'));
const ChatPage = lazyWithRetry(() => import('./components/Chat/ChatPage'));
const InstructorAnalytics = lazyWithRetry(() => import('./components/Admin/InstructorAnalytics'));
const StudentAnalytics = lazyWithRetry(() => import('./components/Admin/StudentAnalytics'));
const InstructorCourses = lazyWithRetry(() => import('./components/Instructor/InstructorCourses'));
const InstructorDetails = lazyWithRetry(() => import('./components/Instructor/InstructorDetails'));
const Announcements = lazyWithRetry(() => import('./components/Announcements/Announcements'));
const AdminAnnouncements = lazyWithRetry(() => import('./components/Announcements/AdminAnnouncements'));
const ApiDocumentation = lazyWithRetry(() => import('./components/Admin/ApiDocumentation'));
const InstructorRefundRequest = lazyWithRetry(() => import('./components/Instructor/RefundRequest'));
const TeamManagement = lazyWithRetry(() => import('./components/Instructor/TeamManagement'));
const StudentPaymentHistory = lazyWithRetry(() => import('./components/payments/StudentPaymentHistory'));
const InstructorPayment = lazyWithRetry(() => import('./components/payments/InstructorPayment'));
const AccessibilityStatement = lazyWithRetry(() => import('./components/Legal/AccessibilityStatement'));
const PrivacyPolicy = lazyWithRetry(() => import('./components/Legal/PrivacyPolicy'));
const RefundCancellationPolicy = lazyWithRetry(() => import('./components/Legal/RefundCancellationPolicy'));
const TermsOfUse = lazyWithRetry(() => import('./components/Legal/TermsOfUse'));
const AdminPaymentHistory = lazyWithRetry(() => import('./components/Admin/AdminPaymentHistory'));
const SubscriptionPlans = lazyWithRetry(() => import('./components/Admin/SubscriptionPlans'));
const SaRefundRequest = lazyWithRetry(() => import('./components/Admin/SaRefundRequest'));
const AccessRequestsAdmin = lazyWithRetry(() => import('./components/Admin/AccessRequestsAdmin'));
const FeaturedCoursesAdmin = lazyWithRetry(() => import('./components/Admin/FeaturedCoursesAdmin'));
const ErrorPage = lazyWithRetry(() => import('./components/Error/ErrorPage'));
const AccessDenied = lazyWithRetry(() => import('./components/Common/AccessDenied'));



// Shown for a moment while a page's code loads (dark, matches the app theme).
const RouteFallback = () => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#05070f' }} aria-busy="true" aria-label="Loading">
    <div style={{ width: 38, height: 38, borderRadius: '50%', border: '3px solid rgba(103,217,230,0.2)', borderTopColor: '#67d9e6', animation: 'route-spin 0.8s linear infinite' }} />
    <style>{'@keyframes route-spin { to { transform: rotate(360deg); } }'}</style>
  </div>
);

// Page wrapper for the Create Course form
const UploadCoursePage = () => {
  const navigate = useNavigate();
  return (
    <Layout title="Create Course" subtitle="Fill in the details to publish a new course for your students">
      <UploadCourse onSuccess={() => navigate('/createdcourses')} />
    </Layout>
  );
};

// Role groups for route-level access control. Instructor pages also admit
// super_admin, matching the checks those pages already performed themselves.
const INSTRUCTOR = ['instructor', 'web_instructor', 'super_admin'];
const STUDENT = ['student'];
const ADMIN = ['super_admin'];

// `roles` omitted = any logged-in user. Otherwise users outside the listed
// roles get the shared AccessDenied screen and the page is never mounted.
const ProtectedRoute = ({ children, roles }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return null; // Could add a spinner later
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (roles && !roles.includes(user?.role)) {
    return <AccessDenied />;
  }

  return children;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <div className="app">
            {/* Global presence pinger: keeps users Online after login */}
            <GlobalPresence />
            <ScrollToTop />
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/pricing" element={<LandingPage page="pricing" />} />
              <Route path="/login" element={<Login />} />
              <Route path="/superadmin" element={<SuperAdminLogin />} />
              <Route path="/register" element={<Register />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/accessibility-statement" element={<AccessibilityStatement />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/refund-and-cancellation-policy" element={<RefundCancellationPolicy />} />
              <Route path="/terms-of-use" element={<TermsOfUse />} />
              <Route path="/subscribe" element={<SubscribeMock />} />
              <Route path="/payments" element={<ProtectedRoute><Payments /></ProtectedRoute>} />
              <Route path="/payments/checkout/:planKey" element={<ProtectedRoute><SubscriptionCheckout /></ProtectedRoute>} />
              <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
              <Route path="/admin/change-user-role" element={<ProtectedRoute roles={ADMIN}><ChangeUserRole /></ProtectedRoute>} />
              <Route path="/admin/courses" element={<ProtectedRoute roles={ADMIN}><ManageCourses /></ProtectedRoute>} />
              <Route path="/admin/users" element={<ProtectedRoute roles={ADMIN}><ManageUsers /></ProtectedRoute>} />
              <Route path="/admin/moderate-content" element={<ProtectedRoute roles={ADMIN}><ModerateContent /></ProtectedRoute>} />
              <Route path="/admin/live-sessions" element={<ProtectedRoute roles={ADMIN}><AdminLiveSessions /></ProtectedRoute>} />
              <Route path="/live-sessions" element={<ProtectedRoute><LiveSessions /></ProtectedRoute>} />
              <Route path="/live-sessions/:id" element={<ProtectedRoute><SessionRoom /></ProtectedRoute>} />
              <Route path="/createdcourses" element={<ProtectedRoute roles={INSTRUCTOR}><CreatedCourses /></ProtectedRoute>} />
              <Route path="/upload-course" element={<ProtectedRoute roles={INSTRUCTOR}><UploadCoursePage /></ProtectedRoute>} />
              <Route path="/browse" element={<ProtectedRoute><BrowseCourses /></ProtectedRoute>} />
              <Route path="/cart" element={<ProtectedRoute roles={STUDENT}><ShoppingCart /></ProtectedRoute>} />
              <Route path="/enroll/preview/:courseId" element={<ProtectedRoute><EnrollmentPreview /></ProtectedRoute>} />
              <Route path="/mycourses" element={<ProtectedRoute roles={STUDENT}><MyCourses /></ProtectedRoute>} />
              <Route path="/learn/:courseId" element={<ProtectedRoute><CourseLearn /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              <Route path="/certifications" element={<ProtectedRoute roles={STUDENT}><Certifications /></ProtectedRoute>} />
              <Route path="/students" element={<ProtectedRoute roles={INSTRUCTOR}><Students /></ProtectedRoute>} />
              <Route path="/student" element={<ProtectedRoute roles={INSTRUCTOR}><Students /></ProtectedRoute>} />
              <Route path="/course-analytics" element={<ProtectedRoute roles={INSTRUCTOR}><CourseAnalytics /></ProtectedRoute>} />
              <Route path="/studentpayments" element={<ProtectedRoute roles={INSTRUCTOR}><StudentPayments /></ProtectedRoute>} />
              <Route path="/calendar" element={<ProtectedRoute><Calendar /></ProtectedRoute>} />
              <Route path="/top-performing-courses" element={<ProtectedRoute roles={INSTRUCTOR}><TopPerforming /></ProtectedRoute>} />
              <Route path="/my-team" element={<ProtectedRoute roles={INSTRUCTOR}><TeamManagement /></ProtectedRoute>} />
              <Route path="/search" element={<ProtectedRoute><SearchResults /></ProtectedRoute>} />
              <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
              <Route path="/instructor-profile" element={<ProtectedRoute><InstructorDetails /></ProtectedRoute>} />
              <Route path="/instructor-profile/:id" element={<ProtectedRoute><InstructorDetails /></ProtectedRoute>} />
              <Route path="/instructor-analysis" element={<ProtectedRoute roles={ADMIN}><InstructorAnalytics /></ProtectedRoute>} />
              <Route path="/student-analysis" element={<ProtectedRoute roles={ADMIN}><StudentAnalytics /></ProtectedRoute>} />
              <Route path="/instructors/:instructorId/courses" element={<ProtectedRoute><InstructorCourses /></ProtectedRoute>} />
              <Route path="/refund-requests" element={<ProtectedRoute roles={INSTRUCTOR}><InstructorRefundRequest /></ProtectedRoute>} />
              <Route path="/payment-history" element={<ProtectedRoute roles={STUDENT}><StudentPaymentHistory /></ProtectedRoute>} />
              <Route path="/instructor/payment-history" element={<ProtectedRoute roles={INSTRUCTOR}><InstructorPayment /></ProtectedRoute>} />
              <Route path="/admin/payment-history" element={<ProtectedRoute roles={ADMIN}><AdminPaymentHistory /></ProtectedRoute>} />
              <Route path="/admin/subscription-plans" element={<ProtectedRoute roles={ADMIN}><SubscriptionPlans /></ProtectedRoute>} />
              <Route path="/admin/refund-requests" element={<ProtectedRoute roles={ADMIN}><SaRefundRequest /></ProtectedRoute>} />
              <Route path="/admin/access-requests" element={<ProtectedRoute roles={ADMIN}><AccessRequestsAdmin /></ProtectedRoute>} />
              <Route path="/admin/featured-courses" element={<ProtectedRoute roles={ADMIN}><FeaturedCoursesAdmin /></ProtectedRoute>} />
              <Route path="/admin/announcements" element={<ProtectedRoute roles={ADMIN}><AdminAnnouncements /></ProtectedRoute>} />
              <Route path="/admin/api-docs" element={<ProtectedRoute roles={ADMIN}><ApiDocumentation /></ProtectedRoute>} />
              <Route path="/announcements" element={<ProtectedRoute roles={INSTRUCTOR}><Announcements /></ProtectedRoute>} />
              <Route path="/error/:code" element={<ErrorPage />} />
              <Route path="/error" element={<ErrorPage code="404" />} />
              <Route path="/" element={<Navigate to="/home" replace />} />
              <Route path="*" element={<ErrorPage code="404" />} />
            </Routes>
            </Suspense>
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;